<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <form-card
            :title="$t(tKey + '.general.title')"
            :description="$t(tKey + '.general.description')"
          >
            <div class="flex flex-col gap-y-5">
              <FormInput
                v-model="rawCompany.name"
                :error="errors?.name"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('company.name')"
                :placeholder="$t('company.name')"
              />
              <Multiselect
                v-model="rawCompany.industry"
                :error="errors?.industry"
                :label="$t('company.industry')"
                :options="fetchIndustries"
                :can-deselect="false"
                :can-clear="false"
                :searchable="true"
              />
              <Multiselect
                v-model="rawCompany.size"
                :error="errors?.size"
                :label="$t('company.size')"
                :options="fetchCompanySizes"
                :can-deselect="false"
                :can-clear="false"
                :searchable="true"
              />
              <FormInput
                v-model="rawCompany.url"
                :error="errors?.url"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('company.url')"
                :placeholder="$t('company.url')"
              />
            </div>
          </form-card>
          <form-card
            :title="$t(tKey + '.contact.title')"
            :description="$t(tKey + '.contact.description')"
          >
            <div class="flex flex-col gap-y-5">
              <FormInput
                v-model="rawCompany.contactFirstName"
                :error="errors?.contactFirstName"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('company.contactFirstName')"
                :placeholder="$t('company.contactFirstName')"
              />
              <FormInput
                v-model="rawCompany.contactLastName"
                :error="errors?.contactLastName"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('company.contactLastName')"
                :placeholder="$t('company.contactLastName')"
              />
              <FormInput
                v-model="rawCompany.contactEmail"
                :error="errors?.contactEmail"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('company.contactEmail')"
                :placeholder="$t('company.contactEmail')"
              />
              <Multiselect
                v-model="rawCompany.contactLanguage"
                :error="errors?.contactLanguage"
                :label="$t('company.contactLanguage')"
                :options="fetchLanguages"
                :can-deselect="false"
                :can-clear="false"
                :searchable="true"
              />
              <FormInput
                v-model="rawCompany.contactTelephone"
                :error="errors?.contactTelephone"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('company.contactTelephone')"
                :placeholder="$t('company.contactTelephone')"
              />
              <FormInput
                v-model="rawCompany.contactAddress.street"
                :error="errors?.contactAddress?.street"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('company.contactAddress.street')"
                :placeholder="$t('company.contactAddress.street')"
              />
              <FormInput
                v-model="rawCompany.contactAddress.cityName"
                :error="errors?.contactAddress?.cityName"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('company.contactAddress.cityName')"
                :placeholder="$t('company.contactAddress.cityName')"
              />
              <FormInput
                v-model="rawCompany.contactAddress.zip"
                :error="errors?.contactAddress?.zip"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('company.contactAddress.zip')"
                :placeholder="$t('company.contactAddress.zip')"
              />
            </div>
          </form-card>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { object, string } from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import FormInput from '@utils/inputs/Input.vue';
import Multiselect from '@utils/Multiselect.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import { enumOptionsProvider } from '@utils/table/hooks/provider';
import { syncRef } from '@hooks/state';
import type { Company } from '@/entities/Entities';

interface Props {
  open: boolean;
  company?: Company;
}

const emit = defineEmits(['update:open', 'update:company', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalCompany = useVModel(props, 'company', emit);

const isNew = computed(() => !props.company);
const tKey = computed(() =>
  (isNew.value ? 'pages.companyList.modal.new' : 'pages.companyList.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const fetchIndustries = enumOptionsProvider('/enums/Industry', (value) => ({
  value,
  label: value,
}));

const fetchCompanySizes = enumOptionsProvider('/enums/CompanySize', (value) => ({
  value,
  label: value,
}));

const fetchLanguages = enumOptionsProvider('/enums/Language', (value) => ({
  value,
  label: t(`language.${value}`),
}));

const initialData = (company?: Company) => (company
  ? {
    ...company,
    contactAddress: {
      street: undefined,
      cityName: undefined,
      zip: undefined,
      ...company.contactAddress,
    },
  }
  : {
    name: undefined,
    industry: undefined,
    size: undefined,
    contactEmail: undefined,
    contactAddress: {
      street: undefined,
      cityName: undefined,
      zip: undefined,
    },
    contactLanguage: undefined,
    contactTelephone: undefined,
    contactFirstName: undefined,
    contactLastName: undefined,
    url: undefined,
  });

const rawCompany = syncRef(internalCompany, (company) => ({
  ...initialData(company),
}));

const {
  data: company,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    name: string().nullable().required(t('validation.notEmpty')),
    industry: string().nullable(),
    size: string().nullable(),
    contactEmail: string().nullable(),
    contactAddress: object().nullable(),
    contactLanguage: string().nullable(),
    contactTelephone: string().nullable(),
    contactFirstName: string().nullable(),
    contactLastName: string().nullable(),
    url: string().nullable(),
  }),
  rawCompany,
);

function resetData() {
  rawCompany.value = { ...initialData() } as unknown as typeof company['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalCompany.value = undefined;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.company;
    const url = edit ? props.company['@id'] : '/companies';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        name: company.value.name,
        industry: company.value.industry,
        size: company.value.size,
        contactEmail: company.value.contactEmail,
        contactAddress: {
          id: company.value.contactAddress?.['@id'] ?? null,
          street: company.value.contactAddress?.street,
          cityName: company.value.contactAddress?.cityName,
          zip: company.value.contactAddress?.zip,
        },
        contactLanguage: company.value.contactLanguage,
        contactTelephone: company.value.contactTelephone,
        contactFirstName: company.value.contactFirstName,
        contactLastName: company.value.contactLastName,
        url: company.value.url,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>
